/**
 * Portal — Meine Dokumente (Mieter/Eigentümer)
 * ==============================================
 * BKA/JA-Dokumente mit Widerspruchs-Countdown und Direkt-Widerspruch aus dem
 * Portal. Nutzt die Endpoints:
 *   GET /api/portal/documents-enriched         (Mieter)
 *   GET /api/portal/owner/documents-enriched   (Eigentümer)
 *   POST /api/portal/challenges                (beide)
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FilePdf, Gavel, Clock, CheckCircle, Warning, Download, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { openAuthenticatedPdf } from "@/lib/pdfUtils";

export default function PortalDokumente() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [challengeTarget, setChallengeTarget] = useState(null);
  const [belegOpen, setBelegOpen] = useState(false);
  // 2026-02 (iter118) — Kenntnisnahme-Panel
  const [pendingAcks, setPendingAcks] = useState([]);

  const loadAcks = async () => {
    try {
      const { data } = await api.get("/portal/pending-acknowledgments");
      setPendingAcks(data || []);
    } catch { setPendingAcks([]); }
  };

  const confirmAck = async (docId) => {
    try {
      await api.post(`/portal/documents/${docId}/acknowledge`, {});
      toast.success("Kenntnisnahme bestätigt");
      loadAcks();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Bestätigen");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const url = isOwner ? "/portal/owner/documents-enriched" : "/portal/documents-enriched";
      const { data } = await api.get(url);
      setDocs(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadAcks(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4" data-testid="portal-dokumente">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <FilePdf size={22} className="text-emerald-700" />
            Meine Dokumente
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Deine Abrechnungen und Unterlagen{isOwner ? " · Widerspruchsfrist bis zur nächsten Versammlung" : " · Widerspruchsfrist 12 Monate (§ 556 BGB)"}
          </p>
        </div>
        {isOwner && (
          <Button variant="outline" onClick={() => setBelegOpen(true)} data-testid="beleg-request-btn"
                  className="gap-1.5">
            <Calendar size={14} /> Belegeinsicht beantragen
          </Button>
        )}
      </div>

      {/* 2026-02 (iter118) — Ausstehende Kenntnisnahmen prominent oben */}
      {pendingAcks.length > 0 && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="ack-banner">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={18} className="text-amber-700" />
            <div className="font-semibold text-amber-900">
              {pendingAcks.length} Dokument{pendingAcks.length > 1 ? "e" : ""} wartet auf Ihre Bestätigung
            </div>
          </div>
          <div className="space-y-1.5">
            {pendingAcks.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 bg-white/70 rounded px-2.5 py-2"
                   data-testid={`ack-item-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{p.title}</div>
                  <div className="text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
                    {p.category && <span className="uppercase tracking-wider text-[9px] font-semibold bg-slate-100 px-1.5 py-0.5 rounded">{p.category}</span>}
                    {p.deadline && (
                      <span className={p.overdue ? "text-rose-700 font-semibold" : (p.days_left != null && p.days_left <= 3 ? "text-amber-700 font-semibold" : "")}>
                        {p.overdue
                          ? `⚠ überfällig seit ${Math.abs(p.days_left)} Tag${Math.abs(p.days_left) !== 1 ? "en" : ""}`
                          : p.days_left != null
                            ? `Frist: noch ${p.days_left} Tag${p.days_left !== 1 ? "e" : ""}`
                            : ""}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => confirmAck(p.id)}
                  className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 gap-1"
                  data-testid={`ack-confirm-${p.id}`}
                >
                  <CheckCircle size={12} /> Bestätigen
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Lade…</div>
      ) : docs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-sm text-slate-500">
          Keine Dokumente vorhanden.
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            const hasCountdown = d.challenge_target_id && d.challenge_days_left != null;
            const critical = hasCountdown && d.challenge_days_left <= 14 && d.challenge_days_left >= 0;
            const expired = hasCountdown && d.challenge_days_left < 0;
            return (
              <div key={d.id} className={`rounded-lg border bg-white p-3 ${critical ? "border-amber-300" : "border-slate-200"}`}
                   data-testid={`portal-doc-${d.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {d.title || d.filename}
                      {d.sub_category === "Betriebskostenabrechnung" && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">BKA</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.category}{d.sub_category ? ` · ${d.sub_category}` : ""}
                      {" · "}{d.created_at?.slice(0, 10)}
                    </div>
                    {hasCountdown && !d.existing_challenge_id && (
                      <div className={`mt-1 text-xs inline-flex items-center gap-1 ${
                        expired ? "text-slate-500" : critical ? "text-amber-800" : "text-slate-600"}`}>
                        <Clock size={12} />
                        {expired ? (
                          <>Frist zum Widerspruch am {d.challenge_deadline_at?.slice(0, 10)} <b>abgelaufen</b></>
                        ) : (
                          <>Frist zum Widerspruch: <b>{d.challenge_days_left} Tage</b> (bis {d.challenge_deadline_at?.slice(0, 10)})</>
                        )}
                      </div>
                    )}
                    {d.existing_challenge_id && (
                      <div className="mt-1 text-xs inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle size={12} /> Widerspruch eingelegt · Status: {d.existing_challenge_status || "offen"}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                            onClick={() => openAuthenticatedPdf(`/documents/${d.id}/download`)}
                            data-testid={`portal-doc-open-${d.id}`}>
                      <Download size={11} /> PDF
                    </Button>
                    {d.challenge_target_id && !d.existing_challenge_id && !expired && (
                      <Button size="sm" className="h-7 text-xs gap-1 bg-amber-600 hover:bg-amber-700"
                              onClick={() => setChallengeTarget(d)}
                              data-testid={`portal-doc-challenge-${d.id}`}>
                        <Gavel size={11} /> Widerspruch
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {challengeTarget && (
        <ChallengeDialog doc={challengeTarget} onClose={() => setChallengeTarget(null)}
                         onSaved={() => { setChallengeTarget(null); load(); }} />
      )}
      {belegOpen && (
        <BelegPruefungRequestDialog onClose={() => setBelegOpen(false)}
                                     onSaved={() => setBelegOpen(false)} />
      )}
    </div>
  );
}

function ChallengeDialog({ doc, onClose, onSaved }) {
  const [subject, setSubject] = useState(`Widerspruch: ${doc.title || doc.filename}`);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!description.trim()) { toast.error("Bitte Begründung eintragen"); return; }
    setSaving(true);
    try {
      await api.post("/portal/challenges", {
        target_type: doc.challenge_target_type,
        target_id: doc.challenge_target_id,
        subject, description,
      });
      toast.success("Widerspruch eingelegt · Verwaltung wurde benachrichtigt");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="portal-challenge-dialog">
        <DialogHeader>
          <DialogTitle>Widerspruch einlegen</DialogTitle>
          <DialogDescription>
            Dein Widerspruch wird sofort an die Verwaltung übermittelt und dort geprüft.
            Du bekommst per E-Mail eine Rückmeldung.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-700 mb-1 block">Betreff</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="challenge-subject" />
          </div>
          <div>
            <label className="text-xs text-slate-700 mb-1 block">Begründung</label>
            <Textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)}
                      placeholder={'z. B. Position „Gartenpflege" mit 850 € erscheint überhöht. Bitte um Prüfung und Beleg-Einsicht.'}
                      data-testid="challenge-description" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || !description.trim()}
                  className="bg-amber-600 hover:bg-amber-700" data-testid="challenge-submit">
            {saving ? "Sende…" : "Widerspruch absenden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BelegPruefungRequestDialog({ onClose, onSaved }) {
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [slots, setSlots] = useState([{ from: "", to: "" }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    // Objekte lesen — Eigentümer sieht seine Objekte via owner-portal Overview
    api.get("/owner-portal/overview").then(({ data }) => {
      const props = data?.properties || [];
      setProperties(props);
      if (props.length === 1) setPropertyId(props[0].id);
    }).catch(() => setProperties([]));
  }, []);
  const patchSlot = (i, key, v) => setSlots(slots.map((s, idx) => idx === i ? { ...s, [key]: v } : s));
  const addSlot = () => setSlots([...slots, { from: "", to: "" }]);
  const removeSlot = (i) => setSlots(slots.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!propertyId) { toast.error("Objekt wählen"); return; }
    const validSlots = slots.filter((s) => s.from && s.to);
    if (validSlots.length === 0) { toast.error("Mindestens einen Slot vorschlagen"); return; }
    setSaving(true);
    try {
      await api.post("/portal/beleg-pruefung", {
        property_id: propertyId,
        proposed_slots: validSlots.map((s) => ({ from: s.from, to: s.to })),
        notes,
      });
      toast.success("Terminanfrage gesendet — Verwaltung bestätigt in Kürze");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="beleg-request-dialog">
        <DialogHeader>
          <DialogTitle>Belegeinsicht beantragen (§ 18 Abs. 4 WEG)</DialogTitle>
          <DialogDescription>
            Schlage 1-3 Zeitfenster vor. Die Verwaltung wählt einen und bestätigt per E-Mail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {properties.length > 1 && (
            <div>
              <label className="text-xs text-slate-700 mb-1 block">Objekt</label>
              <select className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
                      value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
                      data-testid="beleg-property">
                <option value="">wählen…</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-slate-700 mb-1 block">Vorgeschlagene Slots</label>
            {slots.map((s, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 mb-2 items-end">
                <Input type="datetime-local" value={s.from}
                       onChange={(e) => patchSlot(i, "from", e.target.value)}
                       data-testid={`slot-from-${i}`} />
                <div className="flex gap-1">
                  <Input type="datetime-local" value={s.to}
                         onChange={(e) => patchSlot(i, "to", e.target.value)}
                         data-testid={`slot-to-${i}`} />
                  {slots.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-rose-600"
                            onClick={() => removeSlot(i)} data-testid={`slot-remove-${i}`}>✕</Button>
                  )}
                </div>
              </div>
            ))}
            {slots.length < 3 && (
              <Button size="sm" variant="outline" onClick={addSlot} data-testid="slot-add" className="h-7 text-xs">
                + Slot hinzufügen
              </Button>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-700 mb-1 block">Notiz (optional)</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="z. B. Ich möchte insbesondere die Belege für Grundsteuer einsehen."
                      data-testid="beleg-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || !propertyId} data-testid="beleg-submit"
                  className="bg-emerald-700 hover:bg-emerald-800">
            {saving ? "Sende…" : "Anfrage senden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
